// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React, { useState, useRef, useEffect } from "react";

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  accept?: string;
  maxSize?: number; // in MB
  className?: string;
  disabled?: boolean;
  currentFile?: File | null;
  currentImageUrl?: string;
}

const FileUpload = ({
  onFileSelect,
  accept = "image/*",
  maxSize = 10,
  className = "",
  disabled = false,
  currentFile = null,
  currentImageUrl = "",
}: FileUploadProps) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [currentFileUrl, setCurrentFileUrl] = useState<string>("");

  // currentFile이 변경될 때마다 URL 생성 및 정리
  useEffect(() => {
    if (currentFile) {
      const url = URL.createObjectURL(currentFile);
      setCurrentFileUrl(url);

      // 컴포넌트 언마운트 시 또는 currentFile 변경 시 이전 URL 정리
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setCurrentFileUrl("");
    }
  }, [currentFile]);

  const handleFile = (file: File) => {
    // 파일 크기 체크
    if (file.size > maxSize * 1024 * 1024) {
      alert(`파일 크기는 ${maxSize}MB를 초과할 수 없습니다.`);
      return;
    }

    // 파일 타입 체크
    if (!file.type.match(/^image\/(jpg|jpeg|png|gif|webp)$/)) {
      alert("이미지 파일만 업로드할 수 있습니다.");
      return;
    }

    // 파일 선택 콜백 호출
    onFileSelect(file);

    // 미리보기 URL 생성
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;

    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const onButtonClick = (e?: React.MouseEvent) => {
    if (disabled) return;
    e?.preventDefault();
    e?.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleRemove = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onFileSelect(null);
    setPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 현재 표시할 이미지 URL 결정
  // previewUrl > currentFileUrl > currentImageUrl 순으로 우선순위
  const displayUrl = previewUrl || currentFileUrl || currentImageUrl;

  return (
    <div className={`file-upload ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={{ display: "none" }}
        disabled={disabled}
      />

      {displayUrl ? (
        <div className="image-preview">
          <img src={displayUrl} alt="Preview" />
          <div className="image-actions">
            <button
              type="button"
              className="change-image"
              onClick={(e) => onButtonClick(e)}
              disabled={disabled}
            >
              이미지 변경
            </button>
            {/*<button*/}
            {/*  type="button"*/}
            {/*  className="remove-image"*/}
            {/*  onClick={(e) => handleRemove(e)}*/}
            {/*  disabled={disabled}*/}
            {/*>*/}
            {/*  이미지 제거*/}
            {/*</button>*/}
          </div>
        </div>
      ) : (
        <div
          className={`upload-area ${dragActive ? "drag-active" : ""} ${disabled ? "disabled" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={(e) => {
            e.preventDefault();
            onButtonClick(e);
          }}
        >
          <div className="upload-content">
            <div className="upload-icon">📁</div>
            <p>파일을 드래그하거나 클릭하여 업로드</p>
            <p className="upload-hint">
              {accept === "image/*" ? "이미지 파일" : "파일"} (최대 {maxSize}MB)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
